import { useEffect, useMemo } from "react";
import { callAxios, customToast, dev_console, getQueryParamsUrl, isSuccess, whoAmI } from "../../../../Common/ts/commonTools";
import useCustomQuery from "../../../../Common/hooks/useCustomQuery";
import { RTN_interface } from "../../../../Common/interface/App.interface";
import { FileUsageCd_Type, ProductItemPhoto_Type, UPLOAD_STATUS_CD, UploadStatusCd_Type } from "../../static/interface/ProductItemPhoto.interface";
import { defaultApiReaponseInterface } from "../../../../Common/interface/Common.interface";

const TOAST_ID = "usePhotoImageDataListREST";

function usePhotoImageDataListREST (
    {
        get_fields = ["upload_file_detail_data"],
        upload_status_cd = UPLOAD_STATUS_CD.UPLOAD_COMPLETED,
        order_by = 1,
        ...props
    }:{
        brand_id: number;
        data_pk: number;
        file_usage_cd: FileUsageCd_Type;
        get_fields?: string[];
        upload_status_cd?: UploadStatusCd_Type;
        upload_file_pk?: number;
        order_by?: number;
    }
) {
    const isAbleToRequest = useMemo(() => {
        let RTN: boolean = false;

        if(whoAmI.isCustomer() && props.brand_id && props.data_pk){
            RTN = true;
        }

        return RTN;
    }, [props.brand_id, props.data_pk])

    const m_queryKey = useMemo(() => {
        const RTN = ["useProductItemPhotoListREST"];

        if(props.data_pk){
            RTN.push(props.data_pk.toString());
        }

        if(props.brand_id){
            RTN.push(props.brand_id.toString());
        }

        if(get_fields && get_fields.length > 0){
            RTN.push(get_fields.join(","));
        }

        if(props.file_usage_cd){
            RTN.push(props.file_usage_cd.toString());
        }

        if(props.upload_file_pk){
            RTN.push(props.upload_file_pk.toString());
        }

        if(order_by){
            RTN.push(order_by.toString());
        }

        return RTN;
    }, [props.brand_id, props.data_pk, get_fields, props.file_usage_cd, props.upload_file_pk, order_by])

    const {
        data: photoImageDataListData = null,
        error: photoImageDataListError,
        isLoading: photoImageDataListIsLoading,
        refetch: photoImageDataListRefetch,
    } = useCustomQuery({
        queryKey: m_queryKey,
        queryFn: callPhotoImageDataListData,
        enabled: isAbleToRequest,
        select: (response) => {
            const RTN: RTN_interface<ProductItemPhoto_Type | null> = {
                sign: false,
                data: null,
                msg: "",
            };

            SetData:{
                if(!isSuccess(response)){
                    RTN.msg = "사진 데이터 요청과정에서 문제가 발생했습니다.";
                    break SetData;
                }

                const data: defaultApiReaponseInterface<ProductItemPhoto_Type> = response.data;
                RTN.sign = true;
                RTN.data = data.result;
            }

            if(!RTN.sign){
                customToast.error({
                    msg: RTN.msg,
                    toastId: TOAST_ID
                })
            }

            return RTN.data;
        },
    })

    async function callPhotoImageDataListData() {
        const originUrl = "api/v3/common/file-list";

        // 1. URLSearchParams 객체 생성
        const params = new URLSearchParams();

        // 2. 단일 파라미터 추가 (append 사용)
        params.append("brand_id", props.brand_id.toString());
        params.append("data_pk", props.data_pk.toString());
        params.append("upload_status_cd", upload_status_cd.toString());
        params.append("file_usage_cd", props.file_usage_cd.toString());

        if(props.upload_file_pk){
            params.append("upload_file_pk", props.upload_file_pk.toString());
        }

        // 정렬 옵션 (1: 파일 업로드 날짜 순, 2: 파일 업로드 날짜 역순)
        if(order_by){
            params.append("order_by", order_by.toString());
        }

        // 3. 배열 파라미터 반복 추가 (핵심 해결책)
        // append는 같은 키에 대해 값을 덮어쓰지 않고 추가합니다.
        if (Array.isArray(get_fields)) {
            for (const field of get_fields) {
                params.append("get_field[]", field);
            }
        }

        // 4. 최종 URL 생성
        // params.toString()이 자동으로 'key=val&key=val' 형태로 변환하며 인코딩까지 처리합니다.
        const url = `${originUrl}?${params.toString()}`;

        const RTN = await callAxios.api({
            url: url,
            method: "get",
        });

        dev_console.log(url);
        dev_console.log(RTN);

        return RTN;
    }

    useEffect(() => {
        if(photoImageDataListError){
            customToast.error({
                msg: "이미지 데이터 요청 과정에서 문제가 발생했습니다.",
                toastId: TOAST_ID
            })
        }
    }, [photoImageDataListError])

    return({
        photoImageDataListData,
        photoImageDataListError,
        photoImageDataListIsLoading,
        photoImageDataListRefetch,
    })
}

export default usePhotoImageDataListREST;